/**
 * GalleryNavbar
 *
 * The control bar above the screenshot gallery: an Add Screenshot button, a
 * free-text search box, and filters (planet/area, author, sharing status, plus
 * an expandable Tags panel whose header shows the active-tag count). Filter
 * option lists are derived from the loaded library. A Refresh button reloads
 * the library.
 *
 * The trailing icon group also carries the transfer actions: Export bundles up
 * whatever the filters currently show (so the zip matches the screen — set the
 * filters, then export); Export-entire-library and Import are super-admins only,
 * the former taking every screenshot in the library regardless of filters or
 * ownership, the latter bringing a bundle in from another environment.
 */

import { useMemo, useState } from 'react';
import type { TrlImageAsset } from '@swtorbit/shared-socket-events';
import type { GalleryFilters, GallerySort, SharingFilter, SortField } from './useGalleryFilters';

interface GalleryNavbarProps {
  images: TrlImageAsset[];
  filters: GalleryFilters;
  onFiltersChange: (next: GalleryFilters) => void;
  sort: GallerySort;
  onSortChange: (next: GallerySort) => void;
  /** When omitted the Add Screenshot button is hidden (e.g. in image picker context) */
  onAdd?: () => void;
  /** When omitted the Refresh button is hidden */
  onRefresh?: () => void;
  /** When omitted the Stats button is hidden */
  onStats?: () => void;
  /**
   * Export the currently filtered screenshots as a transfer bundle. Omitted (and
   * so hidden) in the image-picker context, where exporting makes no sense.
   */
  onExport?: () => void;
  /**
   * Export the entire library, ignoring filters and including other admins'
   * private screenshots. Omitted (and so hidden) for non-super-admins.
   */
  onExportAll?: () => void;
  /** True while an export is being built, to disable the button and show it. */
  isExporting?: boolean;
  /** Import a transfer bundle. Omitted for non-super-admins (server-gated too). */
  onImport?: () => void;
  resultCount: number;
}

const SHARING_OPTIONS: Array<{ value: SharingFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'private', label: 'Private to me' },
  { value: 'shared-by-me', label: 'Shared by me' },
  { value: 'shared-by-others', label: 'Shared by others' },
];

const SORT_OPTIONS: Array<{ value: SortField; label: string }> = [
  { value: 'createdAt', label: 'Date uploaded' },
  { value: 'area', label: 'Planet / area' },
  { value: 'name', label: 'Name' },
  { value: 'author', label: 'Author' },
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'faction', label: 'Faction' },
];

export function GalleryNavbar({
  images,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  onAdd,
  onRefresh,
  onStats,
  onExport,
  onExportAll,
  isExporting,
  onImport,
  resultCount,
}: GalleryNavbarProps) {
  const areas = useMemo(
    () => [...new Set(images.map((i) => i.area).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [images],
  );
  const authors = useMemo(
    () =>
      [...new Set(images.map((i) => i.createdByName).filter((n): n is string => !!n))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [images],
  );
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const image of images) for (const tag of image.tags) set.add(tag);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [images]);

  const [tagsOpen, setTagsOpen] = useState(false);
  const activeTagCount = filters.tags.length;

  const toggleTag = (tag: string) => {
    const next = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    onFiltersChange({ ...filters, tags: next });
  };

  return (
    <div className="d-flex flex-column gap-xs">
      <div className="d-flex align-items-center gap-xs w-100">
        {onAdd && (
          <button className="btn btn--fancy btn--green text-uppercase fw-bold w-100 mw-12" onClick={onAdd}>Add Screenshot</button>
        )}

        <input
          type="search"
          className="input flex-1"
          placeholder="Search name, area, tags…"
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
        />

        <div className="d-flex align-items-center gap-xs border-md border-cyan rounded-sm p-xs">
          <span className="fs-sm text-slate-dark text-uppercase">Filter</span>

          <select
            className="select w-auto"
            value={filters.area ?? ''}
            onChange={(e) => onFiltersChange({ ...filters, area: e.target.value || null })}
            aria-label="Filter by planet / area"
          >
            <option value="">All planets</option>
            {areas.map((area) => (
              <option key={area} value={area}>{area}</option>
            ))}
          </select>

          <select
            className="select w-auto"
            value={filters.author ?? ''}
            onChange={(e) => onFiltersChange({ ...filters, author: e.target.value || null })}
            aria-label="Filter by author"
          >
            <option value="">All authors</option>
            {authors.map((author) => (
              <option key={author} value={author}>{author}</option>
            ))}
          </select>

          <select
            className="select w-auto"
            value={filters.sharing}
            onChange={(e) => onFiltersChange({ ...filters, sharing: e.target.value as SharingFilter })}
            aria-label="Filter by sharing status"
          >
            {SHARING_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-xs border-md border-cyan rounded-sm p-xs">
          <span className="fs-sm text-slate-dark text-uppercase">Sort</span>
          <select
            className="select w-auto"
            value={sort.field}
            onChange={(e) => onSortChange({ ...sort, field: e.target.value as SortField })}
            aria-label="Sort by"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--outline btn--slate d-flex align-items-center justify-content-center"
            onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
            title={sort.direction === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
            aria-label={sort.direction === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
          >
            {sort.direction === 'asc' ? '▲' : '▼'}
          </button>
        </div>

        <div className="d-flex align-items-center gap-2xs">
          {onRefresh && (
            <button
              className="btn btn--outline btn--slate d-flex align-items-center justify-content-center"
              style={{ minWidth: '3rem', minHeight: '3rem', fontSize: '1.15rem' }}
              onClick={onRefresh}
              title="Reload the image library"
            >🔄</button>
          )}
          {onStats && (
            <button
              className="btn btn--outline btn--slate d-flex align-items-center justify-content-center"
              style={{ minWidth: '3rem', minHeight: '3rem', fontSize: '1.15rem' }}
              onClick={onStats}
              title="View my statistics"
            >ℹ️</button>
          )}
          {onExport && (
            <button
              className="btn btn--outline btn--slate d-flex align-items-center justify-content-center"
              style={{ minWidth: '3rem', minHeight: '3rem', fontSize: '1.15rem' }}
              onClick={onExport}
              disabled={isExporting || resultCount === 0}
              title={
                resultCount === 0
                  ? 'Nothing to export — no screenshots match your filters'
                  : `Export the ${resultCount} filtered screenshot${resultCount === 1 ? '' : 's'} as a bundle you can import elsewhere`
              }
              aria-label="Export filtered screenshots"
            >{isExporting ? '⏳' : '📤'}</button>
          )}
          {onExportAll && (
            <button
              className="btn btn--outline btn--slate d-flex align-items-center justify-content-center"
              style={{ minWidth: '3rem', minHeight: '3rem', fontSize: '1.15rem' }}
              onClick={onExportAll}
              disabled={isExporting}
              title="Export the ENTIRE library as a bundle — every screenshot from every admin, ignoring your filters (including private ones not shown here)"
              aria-label="Export the entire library"
            >{isExporting ? '⏳' : '📦'}</button>
          )}
          {onImport && (
            <button
              className="btn btn--outline btn--slate d-flex align-items-center justify-content-center"
              style={{ minWidth: '3rem', minHeight: '3rem', fontSize: '1.15rem' }}
              onClick={onImport}
              title="Import a Content Library bundle exported from another environment"
              aria-label="Import a content bundle"
            >📥</button>
          )}
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between gap-xs flex-wrap">
        {allTags.length > 0 ? (
          <button
            type="button"
            className="btn btn--sm btn--outline btn--slate d-flex align-items-center gap-2xs"
            onClick={() => setTagsOpen((open) => !open)}
            aria-expanded={tagsOpen}
          >
            <span style={{ display: 'inline-block', transform: tagsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }}>▸</span>
            Tags{activeTagCount > 0 ? ` (${activeTagCount})` : ''}
          </button>
        ) : (
          <span />
        )}

        <span className="fs-sm text-slate-dark">
          {resultCount} screenshot{resultCount === 1 ? '' : 's'}
        </span>
      </div>

      {tagsOpen && allTags.length > 0 && (
        <div className="d-flex flex-wrap gap-2xs align-items-center bg-space bg-o-50 rounded-sm p-xs">
          {allTags.map((tag) => (
            <button
              key={tag}
              className={`btn btn--sm ${filters.tags.includes(tag) ? 'btn--aqua' : 'btn--outline btn--slate'}`}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </button>
          ))}
          {activeTagCount > 0 && (
            <button
              className="btn btn--sm btn--outline btn--slate"
              onClick={() => onFiltersChange({ ...filters, tags: [] })}
            >
              Clear tags
            </button>
          )}
        </div>
      )}
    </div>
  );
}
